import { useGlobalContext } from '@/context/Global/GlobalContext';
import { useSnackbarContext } from '@/context/Snackbar/SnackbarContext';
import { useUpdateImage } from '@/hooks/product/catalog/useUpdateImage';
import { DashboardUserType } from '@/utils/enums';
import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from '@/utils/fileUploadUtils';
import { useEffect, useRef } from 'react';
import { useImmer } from 'use-immer';
import { initialState } from './EditableImageUi';

export function useEditableImageViewController(args: {
  productId: string;
  skuId: string;
  savedImage?: string;
}) {
  const [uiState, setUiState] = useImmer(initialState);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<File>(undefined);
  const updateImageHook = useUpdateImage();
  const { session } = useGlobalContext();
  const { showSnackError } = useSnackbarContext();

  const closeDialog = () =>
    setUiState((draft) => {
      draft.showDialog = false;
    });

  const onClickedTableRowImage = () => {
    if (session?.type !== DashboardUserType.ADMIN) return;
    setUiState((draft) => {
      draft.showDialog = true;
      draft.dialogImage = args.savedImage;
      draft.saveDisabled = true;
    });
  };

  const onClickedEditImage = () => fileInputRef.current?.click();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length <= 0) return;
    const file = event.target.files[0];
    if (file.size > MAX_FILE_SIZE_BYTES) {
      showSnackError(`El archivo no puede ser mayor a ${MAX_FILE_SIZE_MB} MB`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        fileRef.current = file;
        setUiState((draft) => {
          draft.dialogImage = reader?.result?.toString();
          draft.saveDisabled = false;
        });
      }
    };
    reader.readAsDataURL(file);
  };

  const onClickedSave = () => {
    const file = fileRef.current;
    if (!file) return;
    setUiState((draft) => {
      draft.isSaving = true;
      draft.saveDisabled = true;
      draft.cancelDisabled = true;
    });
    updateImageHook.updateImage(
      {
        productId: args.productId,
        skuId: args.skuId,
        file: file,
      },
      {
        onSuccess: () => closeDialog(),
        onSettled: () =>
          setUiState((draft) => {
            draft.isSaving = false;
            draft.saveDisabled = false;
            draft.cancelDisabled = false;
          }),
      }
    );
  };

  useEffect(() => {
    setUiState((draft) => {
      draft.savedImage = args.savedImage;
    });
  }, [args.savedImage]);

  return {
    uiState: {
      ...uiState,
      imageEditable: session?.type === DashboardUserType.ADMIN,
    },
    actions: {
      onClickedTableRowImage,
      onClickedEditImage,
      handleFileChange,
      onClickedSave,
      closeDialog,
    },
    references: {
      fileInputRef,
    },
  };
}
